// ---------------------------------------------------------------------------
// LucidCRM - Service layer
// ---------------------------------------------------------------------------
// ส่วนที่ยิง API จริง (ไม่แก้ Backend): auth / profile / payment QR / orders
// ส่วนโมดูล CRM ที่เหลือ import จาก mock/repositories
// ---------------------------------------------------------------------------
import { ENDPOINTS, PARTNER, CAPTCHA, PROCESS_TYPE, ORDER_TYPE, USER_TYPE } from "./config";
import { http, setToken, setStoredProfile, getStoredProfile, clearSession, ApiError } from "./apiClient";
import { gsEnabled, gsGet, gsPost } from "./gsClient";
import { getBusinessModel } from "../config/business-models";
import { getPlan, monthsOf, nextExpireAt, clampSeats } from "../config/plans";
import { addonModules } from "../config/addons";
import { organizationRepo, subscriptionRepo } from "../mock/repositories";

const ROLE_OVERRIDE_KEY = "levelup:role-override";
const ORG_KEY = "levelup:current-org";
const BRANCH_KEY = "levelup:current-branch";

// --------------------------------------------------------------------------
// Auth (แยกร้านค้าแบบ multi-tenant)
// --------------------------------------------------------------------------
// โครงสร้างบัญชี
//   - admin_crm 1 คน = 1 ร้าน (organizations 1 แถว) สมัครที่ /auth/register
//   - customer สมัครผ่านลิงก์ของร้านเท่านั้น (/auth/register/customer?shop=CODE)
//     และผูกกับ org_id ของร้านนั้นตายตัว — ข้ามร้านไม่ได้
//
// อีเมลเดียวสมัครได้หลายร้าน แต่ PaymentBroker-Backend บังคับอีเมลห้ามซ้ำทั้งระบบ
// จึงยิงเข้า Backend ด้วยอีเมล alias (login_email) เช่น somchai+ab12cd@mail.com
// ส่วนอีเมลจริงเก็บไว้ในชีต users ไว้ค้นหาและแสดงผล
// --------------------------------------------------------------------------
export const resolveUserType = () => localStorage.getItem(ROLE_OVERRIDE_KEY) || USER_TYPE.customer;

// ตัวอักษรรหัสร้าน ตัด I O 0 1 ออกกันอ่านผิด
const SHOP_CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

/** สุ่มรหัสร้าน 6 ตัว (ใช้ตอนเปิดร้านใหม่ และตอนออกรหัสให้ร้านเดิมที่ยังไม่มี) */
export const newShopCode = () =>
  Array.from({ length: 6 }, () => SHOP_CODE_CHARS[Math.floor(Math.random() * SHOP_CODE_CHARS.length)]).join("");

/** อีเมลที่ใช้ยิงเข้า Backend — ผูกรหัสร้านไว้ให้ไม่ซ้ำกันข้ามร้าน */
export const aliasEmail = (email, code) => {
  const [local, domain] = String(email || "").split("@");
  if (!domain) return email;
  return `${local}+${String(code).toLowerCase()}@${domain}`;
};

/** ลิงก์สมัครสมาชิกของร้าน (แอดมินส่งให้ลูกค้า) */
export const shopRegisterLink = (code) => `${window.location.origin}/auth/register/customer?shop=${code}`;

/** อีเมลเดียวมีหลายบัญชีคนละร้าน — หน้า login ต้องให้เลือกร้านก่อน */
export class ShopChoiceError extends ApiError {
  constructor(accounts) {
    super("อีเมลนี้มีบัญชีอยู่หลายร้าน กรุณาเลือกร้านที่ต้องการเข้าใช้งาน");
    this.name = "ShopChoiceError";
    this.accounts = accounts;
  }
}

/**
 * gsPostThenVerify — Apps Script ตอบ POST ด้วยการ redirect ไปหน้า echo ซึ่งบางครั้ง
 * ตอบกลับเป็นหน้า HTML "ขออภัย ไม่สามารถเปิดไฟล์ได้ในขณะนี้" ทั้งที่เขียนลงชีตสำเร็จแล้ว
 * กรณีนี้ห้ามยิงซ้ำ (จะได้ข้อมูลซ้ำ) ให้ไปเช็คสถานะจริงด้วย GET แทน
 */
const gsPostThenVerify = async (action, body, verify) => {
  try {
    return await gsPost(action, body);
  } catch (err) {
    const recovered = await verify().catch(() => null);
    if (recovered) {
      console.warn(`${action}: ตอบกลับไม่สำเร็จ แต่ข้อมูลถูกบันทึกแล้ว —`, err.message);
      return recovered;
    }
    throw err;
  }
};

const requireGs = () => {
  if (!gsEnabled()) throw new ApiError("ยังไม่ได้ตั้งค่า REACT_APP_GS_URL — ระบบแยกร้านต้องใช้ Google Sheets");
};

/** บันทึก/อัปเดตผู้ใช้ลงชีต users (ไม่ทำให้ flow ล่มถ้า Sheets ล่ม) */
const mirrorUserToGs = async (payload) => {
  if (!gsEnabled()) return null;
  try {
    return await gsPost("auth.register", { data: payload });
  } catch (err) {
    console.warn("mirror ผู้ใช้ไป Google Sheets ไม่สำเร็จ:", err.message);
    return null;
  }
};

/** อ่าน user_type จากชีต users (ใช้กับบัญชีเก่าที่สมัครก่อนแยกร้าน) */
const readUserTypeFromGs = async (email) => {
  if (!gsEnabled() || !email) return null;
  try {
    const user = await gsGet("auth.user", { email });
    return user?.user_type || null;
  } catch (err) {
    console.warn("อ่านผู้ใช้จาก Google Sheets ไม่สำเร็จ:", err.message);
    return null;
  }
};

export const authService = {
  /** ข้อมูลร้านจากรหัสในลิงก์สมัคร */
  shopByCode: async (code) => {
    requireGs();
    if (!code) return null;
    return gsGet("auth.shop", { code });
  },

  /** บัญชีทั้งหมดที่ผูกกับอีเมลนี้ (คนละร้าน = คนละบัญชี) */
  accountsOf: async (email) => {
    if (!gsEnabled() || !email) return [];
    try {
      return (await gsGet("auth.accounts", { email })) || [];
    } catch (err) {
      console.warn("อ่านรายการบัญชีจาก Google Sheets ไม่สำเร็จ:", err.message);
      return [];
    }
  },

  login: async ({ email, password, remember = false, captchaToken, loginAs, orgId }) => {
    const accounts = await authService.accountsOf(email);
    const matched = accounts.filter((row) => row.user_type === loginAs);

    // มีหลายร้าน -> ต้องระบุร้าน; ไม่มีบัญชีประเภทที่เลือก -> ไม่ต้องยิง Backend ให้เสียเที่ยว
    let account = null;
    if (matched.length > 1) {
      account = matched.find((row) => row.org_id === orgId) || null;
      if (!account) throw new ShopChoiceError(matched);
    } else if (matched.length === 1) {
      account = matched[0];
    } else if (accounts.length) {
      throw new ApiError(
        loginAs === USER_TYPE.admin
          ? "อีเมลนี้ไม่ใช่บัญชีผู้ดูแลร้าน กรุณาเลือกเข้าสู่ระบบแบบลูกค้า"
          : "อีเมลนี้ยังไม่ได้สมัครเป็นลูกค้าของร้านใด กรุณาสมัครผ่านลิงก์ของร้าน"
      );
    }

    // 1) เข้าสู่ระบบกับ Backend จริง (ได้ token) ด้วยอีเมล alias ของบัญชีนั้น
    const result = await http.post(ENDPOINTS.login, {
      email: account?.login_email || email,
      password: btoa(password),
      remember,
      token: captchaToken || CAPTCHA.masterToken,
    });
    if (!result.status) throw new ApiError(result.message, result);

    setToken(result.data.token);

    // 2) สิทธิ์ยึดจากชีต users เท่านั้น ห้ามยึดปุ่มที่ผู้ใช้กด (loginAs)
    //    บัญชีเก่าที่ยังไม่มีแถวใน users ให้ default เป็น customer (สิทธิ์ต่ำสุด)
    let userType = account?.user_type;
    if (!userType) {
      userType = (await readUserTypeFromGs(email)) || USER_TYPE.customer;
      await mirrorUserToGs({ email, user_type: userType, last_login_at: new Date().toISOString() });
    } else {
      mirrorUserToGs({ email, login_email: account.login_email, last_login_at: new Date().toISOString() });
    }

    localStorage.setItem(ROLE_OVERRIDE_KEY, userType);
    localStorage.removeItem(BRANCH_KEY);
    if (account?.org_id) localStorage.setItem(ORG_KEY, account.org_id);
    else localStorage.removeItem(ORG_KEY);

    const profile = {
      ...result.data,
      password: "",
      email,
      user_type: userType,
      org_id: account?.org_id || "",
      shopName: account?.org_name || "",
      customerId: account?.customer_id || "",
      loginEmail: account?.login_email || email,
    };
    setStoredProfile(profile);
    window.dispatchEvent(new Event("crm-auth-change"));
    return profile;
  },

  /** สมัครฝั่งผู้ดูแล = เปิดร้านใหม่ 1 ร้าน (ผู้สมัครเป็นเจ้าของร้านนั้น) */
  registerAdmin: async ({ fullname, phone, email, address, password, shopName, businessModel }) => {
    requireGs();

    const code = newShopCode();
    const loginEmail = aliasEmail(email, code);

    // 1) สมัครกับ Backend จริง (ใช้อีเมล alias เพราะ Backend บังคับอีเมลห้ามซ้ำ)
    const result = await http.post(ENDPOINTS.register, {
      name_th: fullname,
      name_en: fullname,
      phone,
      email: loginEmail,
      password,
      company_address: address,
    });
    if (!result.status) throw new ApiError(result.message, result);

    // 2) สร้างร้าน + บัญชีผู้ดูแลในชีต
    const model = getBusinessModel(businessModel);
    const created = await gsPostThenVerify(
      "auth.registerAdmin",
      {
        code,
        org: {
          name: shopName,
          nameEn: shopName,
          businessModel: model.key,
          phone,
          email,
          address,
          // ร้านเปิดใหม่ยังไม่มีแพ็กเกจ — ต้องซื้อก่อนถึงจะเข้าใช้งาน /admin ได้
          plan: "",
          planCycle: "",
          planStatus: "unpaid",
          planStartAt: "",
          planExpireAt: "",
          logoColor: "#6366f1",
          enabledModules: model.modules,
          createdAt: new Date().toISOString(),
        },
        data: {
          email,
          login_email: loginEmail,
          name: fullname,
          phone,
          address,
          registered_at: new Date().toISOString(),
        },
      },
      // ถ้าตอบกลับพัง ให้เช็คว่าร้านถูกสร้างไปแล้วหรือยัง
      async () => {
        const shop = await gsGet("auth.shop", { code });
        return shop ? { org: shop, user: null, recovered: true } : null;
      }
    );

    return { ...created, code, loginEmail, registerLink: shopRegisterLink(code) };
  },

  /** สมัครฝั่งลูกค้า — ต้องมารหัสร้านจากลิงก์ของร้านเท่านั้น */
  registerCustomer: async ({ shopCode, fullname, phone, email, address, password }) => {
    requireGs();

    const shop = await gsGet("auth.shop", { code: shopCode });
    if (!shop) throw new ApiError("ไม่พบร้านค้าตามลิงก์นี้ กรุณาขอลิงก์สมัครใหม่จากร้าน");
    if (shop.signupFull) throw new ApiError(`${shop.name} รับสมาชิกเต็มแล้ว กรุณาติดต่อร้านโดยตรง`);

    const accounts = await authService.accountsOf(email);
    if (accounts.some((row) => row.org_id === shop.id)) {
      throw new ApiError(`อีเมลนี้สมัครกับ ${shop.name} ไว้แล้ว กรุณาเข้าสู่ระบบ`);
    }

    const loginEmail = aliasEmail(email, shop.code);

    // 1) สมัครกับ Backend จริง
    const result = await http.post(ENDPOINTS.register, {
      name_th: fullname,
      name_en: fullname,
      phone,
      email: loginEmail,
      password,
      company_address: address,
    });
    if (!result.status) throw new ApiError(result.message, result);

    // 2) สร้างแถว users + customers ใต้ร้านนั้น
    const created = await gsPostThenVerify(
      "auth.registerCustomer",
      {
        code: shop.code,
        data: {
          email,
          login_email: loginEmail,
          name: fullname,
          phone,
          address,
          registered_at: new Date().toISOString(),
        },
      },
      // ตอบกลับพัง = เช็คว่าบัญชีของร้านนี้เกิดขึ้นแล้วหรือยัง
      async () => {
        const rows = await authService.accountsOf(email);
        const mine = rows.find((row) => row.org_id === shop.id);
        return mine ? { user: mine, recovered: true } : null;
      }
    );

    return { ...created, shop };
  },

  forgotPassword: async (email) => {
    const result = await http.post(ENDPOINTS.resetPassword, { email });
    if (!result.status) throw new ApiError(result.message, result);
    return result.data;
  },

  logout: async () => {
    try {
      await http.get(ENDPOINTS.logout);
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem(ROLE_OVERRIDE_KEY);
    localStorage.removeItem(ORG_KEY);
    localStorage.removeItem(BRANCH_KEY);
    clearSession();
    return { success: true };
  },

  currentProfile: () => getStoredProfile(),
  isLoggedIn: () => !!getStoredProfile(),
  isAdmin: () => getStoredProfile()?.user_type === USER_TYPE.admin,
};

// --------------------------------------------------------------------------
// super_user (ทีมงานเจ้าของระบบ) — หน้า /su
// --------------------------------------------------------------------------
//   - login ด้วยบัญชี PaymentBroker ที่ user_type = super_user (อีเมลจริง ไม่ใช่ alias ของร้าน)
//   - สิทธิ์ยึดจาก GET profile ของ Backend เท่านั้น (ค่าใน localStorage แก้เองได้ ห้ามเชื่อ)
//   - Backend เก็บ token ล่าสุดต่อผู้ใช้ 1 ตัว: login ที่นี่จะทำให้ session ใน PaymentBroker-Frontend หลุด
// --------------------------------------------------------------------------
export const superAuth = {
  /** true เมื่อ token ปัจจุบันเป็นของ super_user จริง (ถาม Backend) */
  verify: async () => {
    const result = await http.get(ENDPOINTS.profile);
    return !!result.status && result.data?.user_type === USER_TYPE.superUser;
  },

  login: async ({ email, password, remember = false, captchaToken }) => {
    const result = await http.post(ENDPOINTS.login, {
      email,
      password: btoa(password),
      remember,
      token: captchaToken || CAPTCHA.masterToken,
    });
    if (!result.status) throw new ApiError(result.message, result);
    setToken(result.data.token);

    if (!(await superAuth.verify())) {
      try {
        await http.get(ENDPOINTS.logout);
      } catch (e) {
        /* ignore */
      }
      clearSession();
      throw new ApiError("บัญชีนี้ไม่มีสิทธิ์ super_user");
    }

    localStorage.setItem(ROLE_OVERRIDE_KEY, USER_TYPE.superUser);
    localStorage.removeItem(ORG_KEY);
    localStorage.removeItem(BRANCH_KEY);
    const profile = { ...result.data, token: undefined, password: "", email, user_type: USER_TYPE.superUser, org_id: "" };
    setStoredProfile(profile);
    window.dispatchEvent(new Event("crm-auth-change"));
    return profile;
  },
};

// --------------------------------------------------------------------------
// Profile
// --------------------------------------------------------------------------
const mapProfile = (raw) => {
  if (!raw) return null;
  // ข้อมูลร้าน/ลูกค้าที่ผูกไว้ตอน login อยู่ในชีต ไม่ได้มากับ API โปรไฟล์
  const stored = getStoredProfile() || {};
  return {
    ...raw,
    org_id: raw.org_id || stored.org_id || "",
    shopName: raw.shopName || stored.shopName || "",
    customerId: raw.customerId || stored.customerId || "",
    loginEmail: raw.loginEmail || stored.loginEmail || "",
    uid: raw.customer?.uid || raw.uid,
    userUid: raw.uid,
    customerUid: raw.customer?.uid || "",
    fullname: raw.customer?.name_th || raw.full_name || raw.email || "",
    // แสดงอีเมลจริงที่ผู้ใช้กรอก ไม่ใช่ alias ที่ส่งให้ Backend
    email: stored.email || raw.customer?.email || raw.email || "",
    phone: raw.customer?.phone || raw.phone || "",
    address: raw.customer?.company_address || "",
    avatar: raw.picture || null,
    memberSince: raw.created_at || null,
    isVerified: raw.is_verify,
    user_type: resolveUserType(raw),
  };
};

export const profileService = {
  get: async () => {
    const result = await http.get(ENDPOINTS.profile);
    if (!result.status) return getStoredProfile();
    const stored = getStoredProfile() || {};
    const profile = mapProfile(result.data);
    setStoredProfile({ ...stored, ...result.data, ...profile });
    return profile;
  },

  update: async (payload) => {
    const stored = getStoredProfile();
    const customerUid = stored?.customer?.uid || stored?.customerUid;
    if (!customerUid) throw new ApiError("ไม่พบข้อมูลลูกค้า");

    // ห้ามส่งอีเมลจริงไปทับ alias ที่ Backend ใช้ยืนยันตัวตน ไม่งั้นจะล็อกอินไม่ได้
    // อีเมลจริงเก็บที่ชีต users (คอลัมน์ email) และใน profile ฝั่งเครื่อง
    const result = await http.put(ENDPOINTS.updateCustomer(customerUid), {
      name_th: payload.fullname,
      name_en: payload.fullname,
      phone: payload.phone,
      email: stored.loginEmail || payload.email,
      company_address: payload.address,
    });
    if (!result.status) throw new ApiError(result.message, result);

    const next = { ...stored, ...payload };
    setStoredProfile(next);
    mirrorUserToGs({
      email: next.email,
      login_email: next.loginEmail || "",
      name: payload.fullname,
      phone: payload.phone,
      address: payload.address,
      org_id: next.org_id || "",
    });
    window.dispatchEvent(new Event("crm-auth-change"));
    return next;
  },

  changePassword: async ({ password }) => {
    const stored = getStoredProfile();
    const userUid = stored?.uid || stored?.userUid;
    if (!userUid) throw new ApiError("ไม่พบข้อมูลผู้ใช้");

    const result = await http.put(ENDPOINTS.updateUser(userUid), {
      full_name: stored.full_name,
      phone: stored.phone,
      // เช่นเดียวกับ update: ต้องคงอีเมล alias ที่ใช้ล็อกอินไว้
      email: stored.loginEmail || stored.email,
      role_id: stored.role?.uid,
      // ห้ามส่ง user_type ของ CRM (admin_crm/customer) ไปทับ field เดิมของ Backend
      password,
      is_active: stored.is_active,
      is_2fa: stored.is_2fa,
      is_mismatch_limit_approved: stored.is_mismatch_limit_approved,
    });
    if (!result.status) throw new ApiError(result.message, result);
    return result.data;
  },

  addresses: async () => {
    const stored = getStoredProfile();
    const customerUid = stored?.customer?.uid || stored?.customerUid;
    if (!customerUid) return [];
    const result = await http.get(ENDPOINTS.customerAddress, { uid: customerUid });
    if (!result.status) return [];
    const rows = Array.isArray(result.data) ? result.data : result.data?.address || [];
    return rows.filter((row) => row.is_active !== false);
  },

  saveAddresses: async (rows) => {
    const stored = getStoredProfile();
    const customerUid = stored?.customer?.uid || stored?.customerUid;
    if (!customerUid) throw new ApiError("ไม่พบข้อมูลลูกค้า");

    const result = await http.put(ENDPOINTS.updateCustomer(customerUid), {
      uid: customerUid,
      address_list: rows.map((item) => ({
        ...(item.uid ? { uid: item.uid } : {}),
        contact_name: item.contact_name || "",
        contact_phone: item.contact_phone || "",
        contact_email: item.contact_email || "",
        address: item.address || "",
        sub_district: item.sub_district || "",
        district: item.district || "",
        province: item.province || "",
        zipcode: item.zipcode || "",
        country: item.country || "Thailand",
        is_default: !!item.is_default,
        remark: item.remark || "",
        is_active: item.is_active !== false,
      })),
    });
    if (!result.status) throw new ApiError(result.message, result);
    return rows;
  },
};

// --------------------------------------------------------------------------
// Payment (QR ของจริง) — ใช้ร่วมกันทุก flow ที่มีการจ่ายเงิน
// --------------------------------------------------------------------------
const partnerCredentials = () => ({
  client_id: PARTNER.clientId,
  client_secret: PARTNER.clientSecret,
  client_ref_id: PARTNER.clientRefId,
  "x-apigw-api-id": PARTNER.apigwApiId,
});

const resolveCustomerId = () => {
  const stored = getStoredProfile();
  return stored?.customer?.uid || stored?.customerUid || PARTNER.customerIdDefault;
};

/** บันทึกธุรกรรมลงชีต transactions (ไม่ให้ล้มถ้า Sheets มีปัญหา) */
const mirrorTransactionToGs = async (payload, orgId) => {
  if (!gsEnabled()) return;
  try {
    // orgId ส่งมาตรง ๆ จากหน้าที่เรียก (หน้า /pay สาธารณะไม่มีร้านใน localStorage) ไม่งั้นใช้ร้านที่ล็อกอินอยู่
    await gsPost("create", { table: "transactions", org: orgId || localStorage.getItem(ORG_KEY) || "", data: payload });
  } catch (err) {
    console.warn("mirror ธุรกรรมไป Google Sheets ไม่สำเร็จ:", err.message);
  }
};

/** อัปเดตสถานะธุรกรรมในชีตเมื่อ poll เจอสถานะใหม่ */
const syncTransactionStatusToGs = async (transactionId, status) => {
  if (!gsEnabled() || !status) return;
  try {
    const result = await gsGet("list", { table: "transactions", where: { transactionId } });
    const row = (result?.rows || [])[0];
    if (row && row.status !== status) await gsPost("update", { table: "transactions", id: row.id, data: { status } });
  } catch (err) {
    /* เงียบไว้ ไม่กระทบ flow การจ่ายเงิน */
  }
};

export const paymentService = {
  /**
   * สร้าง QR สำหรับทุก flow (invoice / มัดจำ / checkout / POS / payment link / เติมเงิน / ค่างวด)
   * items = [{ productName, productCode, productImage, quantity, price, detail }]
   */
  createQrOrder: async ({ amount, items = [], remark = "", orderType = ORDER_TYPE.invoice, reference, orgId }) => {
    const customerId = resolveCustomerId();
    if (!customerId) throw new ApiError("ไม่พบรหัสลูกค้า กรุณาเข้าสู่ระบบใหม่");

    const totalAmount = Number(Number(amount).toFixed(2));
    if (!totalAmount || totalAmount <= 0) throw new ApiError("ยอดชำระไม่ถูกต้อง");

    const body = {
      ...partnerCredentials(),
      customer_id: customerId,
      amount: totalAmount,
      ref_no: Date.now().toString(),
      currency: PARTNER.currency,
      order: {
        total_quantity: items.reduce((total, item) => total + (item.quantity || 1), 0) || 1,
        total_price: totalAmount.toFixed(2),
        fee: "0.00",
        order_status: "pending",
        remark,
        process_type: PROCESS_TYPE,
        order_items: (items.length ? items : [{ productName: remark || "ชำระเงิน", quantity: 1, price: totalAmount }]).map((item) => ({
          product_type: orderType,
          product_code: item.productCode || "",
          product_name: item.productName,
          product_image: item.productImage || "",
          quantity: item.quantity || 1,
          price: Number(Number(item.price).toFixed(2)),
          total_price: Number((Number(item.price) * (item.quantity || 1)).toFixed(2)),
          order_type: orderType,
          process_type: PROCESS_TYPE,
          // รายละเอียดเฉพาะของ CRM (อ้างอิงเอกสาร/นัดหมาย) เก็บใน remark
          remark: JSON.stringify({ ...(item.detail || {}), reference, orderType }),
        })),
      },
    };

    const result = await http.post(ENDPOINTS.depositPartner, body);
    if (!result.status) throw new ApiError(result.message, result);

    const gateway = result.data?.data || result.data;
    const gatewayMessage = result.data?.status?.message;
    if (gatewayMessage && gatewayMessage !== "Success") throw new ApiError(gatewayMessage, result);
    if (!gateway?.transaction_id) throw new ApiError("สร้างรายการชำระเงินไม่สำเร็จ", result);

    const created = {
      transactionId: gateway.transaction_id,
      refNo: gateway.ref_no,
      amount: Number(gateway.amount || totalAmount),
      base64: (gateway.base64 || "").replaceAll("_", "+"),
      orderDatetime: gateway.order_datetime,
      expireDatetime: gateway.expire_datetime,
    };

    // mirror ลงชีต transactions (ตัวจริงอยู่ที่ PaymentBroker — ที่นี่เก็บไว้ให้ CRM อ้างอิง)
    mirrorTransactionToGs({
      transactionId: created.transactionId,
      refNo: created.refNo,
      amount: created.amount,
      orderType,
      reference,
      remark,
      status: "pending",
      customerId,
      items: items.map((item) => ({ name: item.productName, quantity: item.quantity || 1, price: item.price })),
      createdAt: new Date().toISOString(),
    }, orgId);

    return created;
  },

  getTransaction: async (transactionId) => {
    const result = await http.get(ENDPOINTS.depositGetById, { uid: transactionId });
    if (!result.status) return null;
    const data = result.data;
    // อัปเดตสถานะล่าสุดลงชีตด้วย
    syncTransactionStatusToGs(transactionId, data.status);
    return {
      uid: data.uid,
      status: data.status,
      amount: Number(data.amount || 0),
      refNo: data.ref_no,
      base64: (data.qrs?.qr_base64 || "").replaceAll("_", "+"),
      expiredAt: data.qrs?.expired_at || null,
    };
  },

  inquiry: async (transactionId) => http.get(ENDPOINTS.inquiry, { uid: transactionId }),
};

// --------------------------------------------------------------------------
// Subscription (แพ็กเกจการใช้งานของร้าน)
// --------------------------------------------------------------------------
// ร้านต้องมีแพ็กเกจที่ยังไม่หมดอายุถึงจะเข้าหน้า /admin ได้
// เงินเข้าผ่าน QR ตัวเดียวกับ flow อื่น (ORDER_TYPE.subscription) เมื่อจ่ายสำเร็จจะ
//   1. เขียนแถวใบเสร็จลงชีต subscriptions (global — มี org_id ในแถว)
//   2. อัปเดต plan / planCycle / planExpireAt ที่แถว organizations
// --------------------------------------------------------------------------
export const subscriptionService = {
  /** ประวัติการชำระค่าแพ็กเกจของร้าน (ใหม่สุดก่อน) */
  history: async (orgId) => {
    if (!orgId) return [];
    const rows = await subscriptionRepo.all(null, (row) => row.org_id === orgId || row.orgId === orgId);
    return [...rows].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  },

  /**
   * ยืนยันการจ่ายเงินค่าแพ็กเกจ — เรียกหลัง QR ตัดเงินสำเร็จเท่านั้น
   * ต่ออายุจากวันหมดอายุเดิมถ้ายังไม่หมด (ไม่ทำให้ลูกค้าเสียวันที่จ่ายไปแล้ว)
   * เรียกซ้ำได้ (ปุ่มลองใหม่ของโมดัล): กันซ้ำด้วย idempotencyKey (ตะกร้า) หรือ transactionId — ไม่ต่ออายุซ้ำ
   */
  activate: async ({ organization, planKey, cycle, seats, amount, transaction, idempotencyKey = "" }) => {
    if (!organization?.id) throw new ApiError("ไม่พบข้อมูลร้าน");
    const plan = getPlan(planKey);
    if (!plan) throw new ApiError("ไม่พบแพ็กเกจที่เลือก");
    // Ultimate เปิดให้โดยทีมงานหลังเซ็นสัญญา ไม่ผ่านการจ่าย QR
    if (plan.quoteOnly) throw new ApiError("แพ็กเกจนี้ต้องขอใบเสนอราคา");
    const planSeats = plan.perUser ? clampSeats(plan.key, seats) : "";

    const transactionId = transaction?.transactionId || "";
    const matchKey = idempotencyKey
      ? (row) => row.idempotencyKey === idempotencyKey
      : transactionId
      ? (row) => row.transactionId === transactionId
      : null;
    const receipt = matchKey ? (await subscriptionRepo.all(null, matchKey))[0] : null;

    // ถ้าเคยออกใบเสร็จแล้ว ใช้วันที่ในใบเสร็จเดิม ห้ามคำนวณวันหมดอายุใหม่ (ไม่งั้นต่ออายุซ้ำ)
    const startAt = receipt?.startAt || new Date().toISOString();
    const expireAt = receipt?.expireAt || nextExpireAt(organization.planExpireAt, cycle);

    // ใบเสร็จก่อน — ถ้าขั้นนี้พังยังกดบันทึกซ้ำได้จากโมดัลโดยไม่ตัดเงินใหม่
    if (!receipt) {
      await subscriptionRepo.create(null, {
        org_id: organization.id,
        orgName: organization.name || "",
        plan: plan.key,
        planName: plan.name,
        seats: planSeats,
        cycle,
        months: monthsOf(cycle),
        amount: Number(amount) || 0,
        status: "paid",
        transactionId,
        ...(idempotencyKey ? { idempotencyKey } : {}),
        refNo: transaction?.refNo || "",
        startAt,
        expireAt,
        createdAt: startAt,
      });
    }

    // ใบเสร็จมีแล้ว: ถ้าร้านถูกอัปเดตตามใบเสร็จไปแล้วไม่ต้องเขียนทับอีก (กันทับโมดูลที่เพิ่งเปิดจากแพ็คเสริมในตะกร้าเดียวกัน)
    const current = receipt ? (await organizationRepo.get(null, organization.id)) || organization : organization;
    if (receipt && current.plan === plan.key && current.planExpireAt && new Date(current.planExpireAt) >= new Date(expireAt)) return current;

    // โมดูลที่เปิดอยู่ต้องไม่เกินสิทธิ์ของแพ็กเกจใหม่ (+ ฟีเจอร์เสริมที่ซื้อไว้และยังไม่หมดอายุ)
    const allowed = [...plan.modules, ...addonModules(current)];
    const keepModules = (current.enabledModules || []).filter((key) => allowed.includes(key));

    const updated = await organizationRepo.update(null, organization.id, {
      plan: plan.key,
      planCycle: cycle,
      planSeats: receipt?.seats || planSeats,
      planStatus: "active",
      planStartAt: current.planStartAt || startAt,
      planExpireAt: expireAt,
      planLastPaidAt: startAt,
      enabledModules: keepModules.length ? keepModules : plan.modules,
    });

    return updated || { ...organization, plan: plan.key, planCycle: cycle, planSeats: receipt?.seats || planSeats, planExpireAt: expireAt };
  },
};

// --------------------------------------------------------------------------
// Orders (ประวัติธุรกรรมจริงจาก Backend)
// --------------------------------------------------------------------------
const parseItemDetail = (item) => {
  try {
    return item?.remark ? JSON.parse(item.remark) : null;
  } catch (e) {
    return null;
  }
};

const mapOrder = (order) => {
  const item = order.order_items?.[0] || {};
  return {
    id: order.uid,
    code: order.order_no,
    status: order.transaction?.status || order.order_status,
    orderType: item.order_type,
    createdAt: order.created_at,
    amount: Number(order.total_price || 0),
    productName: item.product_name || "-",
    transactionUid: order.transaction?.uid || null,
    refNo: order.transaction?.ref_no || "",
    detail: parseItemDetail(item),
  };
};

export const transactionService = {
  list: async ({ page = 1, perPage = 10, orderType, search } = {}) => {
    const params = {
      page,
      page_size: perPage,
      process_type: PROCESS_TYPE,
      order_type: orderType,
      data_search: search,
    };
    const result = await http.get(ENDPOINTS.orders, params);
    if (!result.status) return { rows: [], total: 0, page, perPage };
    return {
      rows: (result.data || []).map(mapOrder),
      total: result.pagination?.count ?? (result.data || []).length,
      page,
      perPage,
    };
  },
};

export * from "../mock/repositories";
export * from "./addons";
export * from "./superAdmin";
export * from "./gsClient";
export * from "./apiClient";
export * from "./config";
